import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useWebSocket from '../../hooks/useWebSocket';
import useWakeLock from '../../hooks/useWakeLock';
import { participantService } from '../../services';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Content Page - Participant
 * View session content with subsessions
 * Dashboard-style layout with sidebar navigation
 */

const WorkbookViewerPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const socket = useWebSocket(sessionId);
  // Fix #9: keep screen awake while reading workbook during a live session
  useWakeLock(true);
  const [session, setSession] = useState(null);
  const [subsessions, setSubsessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActivityStartedDialog, setShowActivityStartedDialog] = useState(false);
  const [activityInfo, setActivityInfo] = useState(null);
  const [showNotLiveDialog, setShowNotLiveDialog] = useState(false);
  const [selectedSubsession, setSelectedSubsession] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Safe navigation helper to validate sessionId before navigating
  const safeNavigate = (path) => {
    if (!sessionId || sessionId === 'undefined') {
      console.error('[Content] Invalid sessionId for navigation:', sessionId);
      return;
    }
    console.log('[Content] Navigating to:', path);
    navigate(path);
  };

  useEffect(() => {
    // Fetch session data from API
    const fetchSession = async () => {
      try {
        setLoading(true);
        const response = await participantService.getSession(sessionId);
        if (response.success) {
          const sessionData = response.data;
          setSession(sessionData);

          // Use actual subSessions from session data if available
          if (sessionData.subSessions && sessionData.subSessions.length > 0) {
            setSubsessions(sessionData.subSessions);
          } else {
            setSubsessions([]);
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  // Listen for activity launched events
  useEffect(() => {
    if (!socket || !sessionId) return;

    const handleActivityLaunched = (data) => {
      console.log('[Content] Activity launched:', data);
      setActivityInfo(data);
      setShowActivityStartedDialog(true);
    };

    socket.on('activity:launched', handleActivityLaunched);

    return () => {
      socket.off('activity:launched', handleActivityLaunched);
    };
  }, [socket, sessionId]);

  const handleGoToActivity = () => {
    setShowActivityStartedDialog(false);
    safeNavigate(`/session/${sessionId}/active`);
  };

  // Navigation items for sidebar (order: Welcome, Aktivity, Notes, Content, Reports)
  const navigationItems = [
    {
      id: 'welcome',
      name: 'Welcome',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => safeNavigate(`/session/${sessionId}/active?view=welcome`),
    },
    {
      id: 'activities',
      name: 'Aktivities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      onClick: () => safeNavigate(`/session/${sessionId}/active`),
      badge: session?.status === 'live' ? 'Live' : null,
    },
    {
      id: 'workspace',
      name: 'My Workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => safeNavigate(`/session/${sessionId}/notes`),
    },
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      onClick: () => {},
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => safeNavigate(`/session/${sessionId}/reports`),
    },
  ];

  const bottomNavItems = [
    {
      id: 'help',
      name: 'Help Center',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // Helper to render activity icon with styled container
  const renderActivityIcon = (type) => (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActivityTypeColor(type)}`}>
      {getActivityIcon(type, 'sm')}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
          <p className="text-neutral-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Session not found</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img
                  src={logoFull}
                  alt="Aktivity"
                  className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Back Button — returns participant to the lobby's welcome view with phase sidebar */}
        <div className="px-3 pt-2">
          <button
            onClick={() => navigate(`/session/${sessionId}/lobby?view=welcome`)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-all text-sm"
            title={sidebarCollapsed ? 'Back to lobby' : ''}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {!sidebarCollapsed && <span>Back</span>}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    item.id === 'content'
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {item.id === 'content' && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${item.id === 'content' ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg
              className="w-5 h-5 transition-transform"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={() => safeNavigate(`/session/${sessionId}/lobby`)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            </div>

            {/* Title */}
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">Content</h1>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                session?.status === 'live' ? 'bg-green-50 text-green-700' :
                session?.status === 'completed' ? 'bg-blue-50 text-blue-700' :
                'bg-neutral-50 text-neutral-700'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  session?.status === 'live' ? 'bg-green-500 animate-pulse' :
                  session?.status === 'completed' ? 'bg-blue-500' :
                  'bg-neutral-400'
                }`}></div>
                <span className="text-xs font-medium hidden sm:inline capitalize">{session?.status || 'Loading'}</span>
              </div>
              {session?.code && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                  <span className="text-xs text-neutral-500">Code:</span>
                  <span className="text-xs font-mono font-semibold text-neutral-900">{session.code}</span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-y-auto pb-20 lg:pb-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">Session Content</h2>
              <p className="text-neutral-500 mt-1">{session.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-neutral-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <p className="text-xs text-neutral-400 mt-1">
                {subsessions.length} {subsessions.length === 1 ? 'sub-session' : 'sub-sessions'} available
              </p>
            </div>
          </div>

          {/* Session Overview Card */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">{session.name}</h3>
                {session.description && (
                  <p className="text-sm text-neutral-500 mt-1">{session.description}</p>
                )}
              </div>
              <span className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                session?.status === 'live' ? 'bg-green-100 text-green-700' :
                session?.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                'bg-neutral-100 text-neutral-700'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  session?.status === 'live' ? 'bg-green-500 animate-pulse' :
                  session?.status === 'completed' ? 'bg-blue-500' :
                  'bg-neutral-400'
                }`}></span>
                {session?.status === 'live' ? 'Live' : session?.status === 'completed' ? 'Completed' : session?.status || 'Unknown'}
              </span>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-neutral-50 rounded-lg">
                <p className="text-2xl font-bold text-neutral-900">{subsessions.length}</p>
                <p className="text-xs text-neutral-500">Sub-sessions</p>
              </div>
              <div className="text-center p-3 bg-neutral-50 rounded-lg">
                <p className="text-2xl font-bold text-neutral-900">
                  {subsessions.reduce((total, sub) => total + (sub.activities?.length || 0), 0)}
                </p>
                <p className="text-xs text-neutral-500">Aktivities</p>
              </div>
              {(() => {
                // Calculate total duration only from subsessions with startTime and endTime set
                const subsessionsWithTime = subsessions.filter(sub => sub.startTime && sub.endTime);
                if (subsessionsWithTime.length === 0) return null;

                const totalMinutes = subsessionsWithTime.reduce((total, sub) => {
                  const duration = sub.duration || Math.round((new Date(sub.endTime) - new Date(sub.startTime)) / 60000);
                  return total + duration;
                }, 0);
                if (totalMinutes === 0) return null;
                const hours = Math.floor(totalMinutes / 60);
                const mins = totalMinutes % 60;
                const durationStr = hours > 0 && mins > 0 ? `${hours}h ${mins}m` : hours > 0 ? `${hours}h` : `${mins}m`;
                return (
                  <div className="text-center p-3 bg-neutral-50 rounded-lg">
                    <p className="text-2xl font-bold text-neutral-900">{durationStr}</p>
                    <p className="text-xs text-neutral-500">Duration</p>
                  </div>
                );
              })()}
            </div>

            {/* Scheduled Time */}
            {(session.startTime || session.scheduledDate) && (
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    {session.startTime && session.endTime ? (
                      <>
                        {new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {' - '}
                        {new Date(session.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {session.duration && ` (${session.duration} min)`}
                      </>
                    ) : session.duration ? (
                      `Duration: ${session.duration} minutes`
                    ) : (
                      new Date(session.scheduledDate).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Tags */}
            {session.tags && session.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-neutral-100">
                {session.tags.map((tag, index) => (
                  <span key={index} className="px-2.5 py-1 bg-neutral-100 text-neutral-600 text-xs font-medium rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Training Agenda */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-neutral-900 mb-4">Training Agenda</h3>

            {subsessions.length === 0 ? (
              <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-7 h-7 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-neutral-900 mb-1">No Content Available</p>
                <p className="text-xs text-neutral-500">The trainer hasn't added any sub-sessions yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {subsessions.map((subsession, index) => (
                  <div key={subsession.id} className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    {/* Module Header */}
                    <div className="p-4" style={{ backgroundColor: '#ff8a00' }}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-white">{subsession.name || subsession.title}</h4>
                            <div className="flex items-center gap-3 mt-1">
                              {(() => {
                                // Only show duration if startTime and endTime are explicitly set
                                if (subsession.startTime && subsession.endTime) {
                                  const totalMinutes = subsession.duration || Math.round((new Date(subsession.endTime) - new Date(subsession.startTime)) / 60000);
                                  if (totalMinutes > 0) {
                                    const hours = Math.floor(totalMinutes / 60);
                                    const mins = totalMinutes % 60;
                                    const durationStr = hours > 0
                                      ? (mins > 0 ? `${hours}h ${mins}m` : `${hours}h`)
                                      : `${mins} min`;
                                    return (
                                      <span className="text-xs text-white text-opacity-80">
                                        {durationStr}
                                      </span>
                                    );
                                  }
                                }
                                return null;
                              })()}
                              {subsession.activities && subsession.activities.length > 0 && (
                                <>
                                  {(subsession.startTime && subsession.endTime) && (
                                    <span className="text-white text-opacity-50">•</span>
                                  )}
                                  <span className="text-xs text-white text-opacity-80">
                                    {subsession.activities.length} {subsession.activities.length === 1 ? 'activity' : 'activities'}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {subsession.status === 'live' && (
                          <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full uppercase">
                            Live
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Activities */}
                    {subsession.activities && subsession.activities.length > 0 && (
                      <div className="p-4">
                        <p className="text-xs font-medium text-neutral-500 mb-3">Activities in this module:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {subsession.activities.map((activity) => (
                            <div key={activity.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                              {renderActivityIcon(activity.type)}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-neutral-900 truncate">{activity.name}</p>
                                <p className="text-xs text-neutral-500 capitalize">{activity.type.replace(/([A-Z])/g, ' $1').trim()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Show all items with smaller sizes */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                item.id === 'content' ? '' : 'text-neutral-500'
              }`}
              style={item.id === 'content' ? { color: '#ff8a00' } : {}}
            >
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Activity Started Dialog */}
      <ConfirmDialog
        isOpen={showActivityStartedDialog}
        onClose={() => setShowActivityStartedDialog(false)}
        onConfirm={handleGoToActivity}
        title={`${activityInfo?.activityType === 'poll' ? 'Poll' : activityInfo?.activityType === 'quiz' ? 'Quiz' : 'Aktivity'} Started!`}
        message={`${activityInfo?.activityTitle || 'An aktivity'} has been launched. Go back to aktivities to participate.`}
        confirmText="Go to Aktivity"
        cancelText="Stay Here"
        variant="info"
      />

      {/* Session Not Live Dialog */}
      <ConfirmDialog
        isOpen={showNotLiveDialog}
        onClose={() => setShowNotLiveDialog(false)}
        onConfirm={() => setShowNotLiveDialog(false)}
        title="Session Not Started Yet"
        message={`"${selectedSubsession?.title || 'This session'}" hasn't started yet. Please wait for the trainer to start the session.`}
        confirmText="OK"
        cancelText={null}
        variant="info"
      />
    </div>
  );
};

export default WorkbookViewerPage;
